import { TeamMemberStatus } from '@/shared/api';
import type { StatusTone } from '@/shared/ui';
import { type Me, MemberRole } from '../api/schemas';

/** Ключ перевода названия роли (common:roles.*). */
export const ROLE_LABEL_KEYS = {
  [MemberRole.Admin]: 'roles.admin',
  [MemberRole.Winemaker]: 'roles.winemaker',
  [MemberRole.Expert]: 'roles.expert',
} as const satisfies Record<MemberRole, string>;

/** Имя участника для подвала сайдбара: ФИО или почта. */
export function memberDisplayName(me: Me): string {
  const name = me.user.full_name?.trim();
  return name ? name : me.user.email;
}

/** Инициал для аватара. */
export function memberInitial(me: Me): string {
  return memberDisplayName(me).charAt(0).toUpperCase();
}

/**
 * «Владельческие» разделы (ТЗ ACC 4.2.2.2): для winemaker/expert реестр фильтруется сервером
 * по OwnershipScope, а над ним показывается плашка области видимости.
 */
export const OWNERSHIP_SECTIONS = [
  'wineries',
  'wines',
  'sets',
  'drops',
  'courses',
  'lessons',
  'tests',
  'tastings',
  'calibs',
  'excursions',
  'trips',
  'orders',
  'deliveries',
  'bookings',
  'publications',
  'stories',
  'docs',
] as const;

export function isOwnershipSection(section: string): boolean {
  return OWNERSHIP_SECTIONS.some((key) => key === section);
}

/** Роль видит только свои объекты (плашка области видимости) — винодел и эксперт. */
export function hasOwnershipScope(me: Me): boolean {
  return me.member.role !== MemberRole.Admin;
}

/** Статус участника (TeamMember.status, ТЗ 4.5.2) → подпись (common:memberStatus.*) и тон пилюли. */
export function memberStatusTone(status: TeamMemberStatus): StatusTone {
  switch (status) {
    case TeamMemberStatus.Active:
      return 'ok';
    case TeamMemberStatus.PendingReview:
    case TeamMemberStatus.NeedsInfo:
      return 'warn';
    case TeamMemberStatus.Rejected:
    case TeamMemberStatus.Blocked:
      return 'bad';
    default: {
      const unknown: never = status;
      return unknown;
    }
  }
}

export const MEMBER_STATUS_LABEL_KEYS = {
  [TeamMemberStatus.Active]: 'memberStatus.active',
  [TeamMemberStatus.PendingReview]: 'memberStatus.pending_review',
  [TeamMemberStatus.NeedsInfo]: 'memberStatus.needs_info',
  [TeamMemberStatus.Rejected]: 'memberStatus.rejected',
  [TeamMemberStatus.Blocked]: 'memberStatus.blocked',
} as const satisfies Record<TeamMemberStatus, string>;
